import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { resolveReadMedia } from '../social-post/socialPostMediaModel';
import { normalizeQuotedPostReference } from '../social-post/socialQuoteReferenceModel';

function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

function nonNegative(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function kindLabel(postKind) {
  const kind = String(postKind || '').trim();
  return kind || 'POST';
}

export function followingFeedErrorMessage(cause) {
  const key = String(cause?.raw?.key || '');
  const code = cause?.raw?.code ?? cause?.status ?? cause?.code;
  if (code === 401 || /unauthor/i.test(String(cause?.message || '')) || key.includes('unauthorized')) {
    return '請先登入後再查看追蹤動態。';
  }
  if (code === 403 || key.includes('permission') || key.includes('forbidden')) {
    return '目前無法查看這份追蹤動態。';
  }
  if (code === 96100 || key === 'travel.social.timeline.cursor.invalid') {
    return '列表位置已失效，請重新整理。';
  }
  return resolveSafeUserVisibleError(cause, () => '無法載入追蹤動態，請稍後再試。', 'travel.following.errorTitle');
}

export function normalizeFollowingFeedItem(raw) {
  const postId = snowflakeId(raw?.postId);
  if (!postId) return null;
  const author = raw?.author || {};
  const journey = raw?.linkedJourneyPublication;
  const location = raw?.linkedLocation?.available ? raw.linkedLocation : null;
  const quote = normalizeQuotedPostReference(raw?.quotedPost, raw?.quotedSocialPostId);
  return {
    postId,
    kind: kindLabel(raw?.postKind),
    text: raw?.textContent || '',
    publishedAt: raw?.publishedAt || null,
    coverUrl: resolveMemberMediaUrl(raw?.coverFileReference),
    media: resolveReadMedia(raw),
    author: {
      memberId: snowflakeId(author.memberId) || snowflakeId(raw?.authorMemberId),
      displayName: author.displayName || 'Traveler',
      avatarUrl: resolveMemberMediaUrl(author.avatarUrl),
    },
    journey: journey?.available ? {
      publicationId: snowflakeId(journey.publicationId),
      title: journey.title || 'Shared journey',
      summary: journey.summary || '',
      coverUrl: resolveMemberMediaUrl(journey.coverFileReference),
    } : null,
    journeyUnavailable: Boolean(journey && journey.available === false),
    location: location ? {
      locationId: snowflakeId(location.locationId),
      displayName: location.displayName || 'Shared location',
    } : null,
    quote,
    engagement: {
      likeCount: nonNegative(raw?.likeCount),
      saveCount: nonNegative(raw?.saveCount),
      commentCount: nonNegative(raw?.commentCount),
      liked: Boolean(raw?.liked),
      saved: Boolean(raw?.saved),
      followingAuthor: Boolean(raw?.followingAuthor),
    },
  };
}

export function normalizeFollowingFeedPage(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeFollowingFeedItem).filter(Boolean)
    : [];
  return {
    items,
    nextCursor: payload?.nextCursor || null,
    hasMore: Boolean(payload?.hasMore),
  };
}

export function appendFollowingFeedItems(current, incoming) {
  const seen = new Set((Array.isArray(current) ? current : []).map((item) => String(item.postId)));
  const extra = (Array.isArray(incoming) ? incoming : []).filter((item) => {
    const id = String(item.postId);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return [...current, ...extra];
}
